import React, { useState, useEffect, useRef } from "react";
import {
    Dialog, DialogHeader, DialogBody, DialogFooter,
    Typography, Button, IconButton, Input, Option, Select, Avatar
} from "@material-tailwind/react";
import { CheckCircleIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import getData from "../../../api/axiosInstance";

export default function EditRefundDialog({ isOpen, onClose, onSuccess, requestHeaders, refundId }) {

    const [refundToEdit, setRefundToEdit] = useState({});
    const [allItemList, setAllItemList] = useState([]);
    const [isEditDropdownOpen, setIsEditDropdownOpen] = useState(false);
    const [editSearchTerm, setEditSearchTerm] = useState("");
    const [filteredItemsEdit, setFilteredItemsEdit] = useState([]);
    const [deleteTimer, setDeleteTimer] = useState(null);

    const editSearchInputRef = useRef(null);
    const timerRef = useRef(null);

    // Fetch refund data when dialogs opens
    useEffect(() => {
        if (isOpen && refundId) {
            const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
            axios
                .get(`${API_URL}/refunded_items/${refundId}`, { headers: requestHeaders })
                .then(response => setRefundToEdit(response.data))
                .catch(error => console.error("Error fetching refund data:", error));
        }
    }, [isOpen, refundId, requestHeaders]);

    // Fetch all items for dropdown
    useEffect(() => {
        if (isOpen) {
            getData(
                "/purchased_items/all_legacy",
                requestHeaders,
                setAllItemList,
                "Error fetching item data"
            );
        }
    }, [isOpen, requestHeaders]);

    // Filter items for edit dropdown
    useEffect(() => {
        setFilteredItemsEdit(
            allItemList.filter((manga) =>
                manga.title.toLowerCase().includes(editSearchTerm.toLowerCase())
            )
        );
    }, [editSearchTerm, allItemList]);

    const handleInput = (e) => {
        const { name, value } = e.target;
        setRefundToEdit(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            if (!refundToEdit.purchasedItem || !refundToEdit.purchasedItem.id) return;

            const updatePayload = {
                ...refundToEdit,
                purchasedItem: { id: refundToEdit.purchasedItem.id },
            };

            const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

            await axios.put(
                `${API_URL}/refunded_items/edit/${refundToEdit.id}`,
                updatePayload,
                { headers: requestHeaders }
            );

            onSuccess();
            onClose();

        } catch (err) {
            console.error("Error updating refund:", err);
        }
    };

    const handleDelete = async () => {
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
        if (!refundToEdit?.id) return;

        try {
            await axios.delete(
                `${API_URL}/refunded_items/delete/${refundToEdit.id}`,
                { headers: requestHeaders }
            );

            onSuccess();
            onClose();
            setDeleteTimer(null);

        } catch (err) {
            console.error("Error deleting refund:", err);
        }
    };

    const startDeleteCountdown = () => {
        if (deleteTimer !== null) {
            clearInterval(timerRef.current);
            setDeleteTimer(null);
            return;
        }

        let timeLeft = 5;
        setDeleteTimer(timeLeft);

        timerRef.current = setInterval(() => {
            timeLeft -= 1;
            setDeleteTimer(timeLeft);

            if (timeLeft === 0) {
                clearInterval(timerRef.current);
                handleDelete();
            }
        }, 1000);
    };

    const handleClose = () => {
        setDeleteTimer(null);
        if (timerRef.current) clearInterval(timerRef.current);
        setEditSearchTerm("");
        setIsEditDropdownOpen(false);
        onClose();
    };

    return (
        <Dialog
            size="l"
            open={isOpen}
            handler={handleClose}
            className="p-4 mt-10 mb-10 flex flex-col h-[80vh] max-h-[80vh] overflow-hidden"
        >
            <DialogHeader className="relative m-0 block">
                <Typography variant="h4" color="blue-gray">
                    Edit refund
                </Typography>
                <Typography className="mt-1 font-normal text-gray-600">
                    Update refunded item.
                </Typography>
                <IconButton
                    size="sm"
                    variant="text"
                    className="!absolute right-3.5 top-3.5"
                    onClick={handleClose}
                >
                    <XMarkIcon className="h-4 w-4 stroke-2" />
                </IconButton>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
                <DialogBody className="space-y-4 overflow-y-auto flex-grow max-h-[50vh] mb-4">
                    <div className="relative">
                        <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                            Choose Item
                        </Typography>
                        <button
                            type="button"
                            className="w-full bg-white border border-blue-gray-200 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-4 focus:ring-blue-500 focus:border-blue-500"
                            onClick={() => setIsEditDropdownOpen((prev) => !prev)}
                        >
                            {refundToEdit.purchasedItem ? (
                                <div className="flex items-center">
                                    <Avatar
                                        className="w-10 h-10 rounded-full"
                                        src={allItemList.find((manga) => manga.id === refundToEdit.purchasedItem?.id)?.imageUrl}
                                        alt="Item"
                                    />
                                    <div className="ml-4">
                                        <p className="text-sm font-medium text-gray-900">
                                            {allItemList.find((manga) => manga.id === refundToEdit.purchasedItem?.id)?.title}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <span className="text-gray-500">Select Item</span>
                            )}
                        </button>
                        {isEditDropdownOpen && (
                            <div className="absolute z-10 mt-2 w-full bg-white border border-blue-gray-200 rounded-md shadow-lg max-h-80 overflow-auto focus:outline-none">
                                <div className="relative p-2 border-b border-blue-gray-200">
                                    <input
                                        ref={editSearchInputRef}
                                        type="text"
                                        placeholder="Search Item"
                                        value={editSearchTerm}
                                        onChange={(e) => setEditSearchTerm(e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    {editSearchTerm && (
                                        <button
                                            type="button"
                                            onClick={() => setEditSearchTerm("")}
                                            className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <XMarkIcon className="h-5 w-5" />
                                        </button>
                                    )}
                                </div>
                                <ul className="max-h-60 overflow-y-auto">
                                    {filteredItemsEdit.map((manga) => (
                                        <li
                                            key={manga.id}
                                            onClick={() => {
                                                handleInput({ target: { name: "purchasedItem", value: manga.id } });
                                                setIsEditDropdownOpen(false);
                                            }}
                                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                                        >
                                            <div className="flex items-center">
                                                <Avatar className="w-10 h-10 rounded-full" src={manga.imageUrl} alt={manga.title} />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-gray-900">{manga.title}</p>
                                                    <p className="text-sm text-gray-500">
                                                        {new Date(manga.purchasedDate).toLocaleString("en-US", {
                                                            year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
                                                        })}
                                                    </p>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                {filteredItemsEdit.length === 0 && (
                                    <div className="p-3 text-center text-gray-500">No items found</div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                Paid price (GBP)
                            </Typography>
                            <Input
                                type="number"
                                step="0.01"
                                color="gray"
                                size="lg"
                                placeholder="eg. 100.00"
                                name="paidPrice"
                                value={refundToEdit.paidPrice ?? ''}
                                onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900"
                                containerProps={{ className: "!min-w-full" }}
                                labelProps={{ className: "hidden" }}
                            />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                Refunded Amount (RSD)
                            </Typography>
                            <Input
                                type="number"
                                step="0.01"
                                color="gray"
                                size="lg"
                                placeholder="eg. 100.00"
                                name="refundedAmount"
                                value={refundToEdit.refundedAmount ?? ''}
                                onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900"
                                containerProps={{ className: "!min-w-full" }}
                                labelProps={{ className: "hidden" }}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                Status
                            </Typography>
                            <Select
                                name="status"
                                className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                labelProps={{ className: "hidden" }}
                                onChange={(value) => handleInput({ target: { name: 'status', value } })}
                                value={refundToEdit.status || ''}
                            >
                                <Option value="ISSUED">Issued</Option>
                                <Option value="COMPLETED">Completed</Option>
                                <Option value="CANCELED">Canceled</Option>
                            </Select>
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                Refund date
                            </Typography>
                            <Input
                                type="datetime-local"
                                color="gray"
                                size="lg"
                                placeholder="Select date and time"
                                name="refundDate"
                                value={refundToEdit.refundDate || ''}
                                onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900"
                                containerProps={{ className: "!min-w-full" }}
                                labelProps={{ className: "hidden" }}
                            />
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter className="flex items-center justify-between gap-2">
                    <Button
                        onClick={startDeleteCountdown}
                        className={deleteTimer !== null ? "mv-warning-button" : "mv-danger-button"}
                    >
                        <TrashIcon className="h-4 w-4" />
                        {deleteTimer !== null ? `Abandon (${deleteTimer})` : "Delete"}
                    </Button>
                    <Button type="submit" className="mv-primary-button">
                        <CheckCircleIcon className="h-4 w-4" />
                        Save
                    </Button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}
